import { booleanAttribute, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideChevronDown, lucideTrash } from '@ng-icons/lucide';
import { ExpansionPanel, ExpansionPanelMenuAction } from '@shared/components/expansion-panel/expansion-panel.component';
import { BrnCommandImports } from '@spartan-ng/brain/command';
import { BrnPopoverImports } from '@spartan-ng/brain/popover';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmAutocompleteImports, provideHlmAutocompleteConfig } from '@spartan-ng/helm/autocomplete';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmButtonGroupImports } from '@spartan-ng/helm/button-group';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmCommandImports } from '@spartan-ng/helm/command';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { HlmRadioGroupImports } from '@spartan-ng/helm/radio-group';
import { HlmSelectImports } from '@spartan-ng/helm/select';

import { Aptitude } from '../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { AptitudeStore } from '../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/store/aptitude.store';
import { TrierParPipe } from '../../../shared/pipes/trier-par.pipe';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';
import { ReferentielStore } from '../../../administration/gestion-référentiels/store/referentiel.store';

@Component({
  selector: 'app-aptitude-group',
  imports: [
    ReactiveFormsModule,
    HlmIconImports,
    HlmDropdownMenuImports,
    TrierParPipe,
    BrnPopoverImports,
    HlmSelectImports,
    HlmCommandImports,
    HlmRadioGroupImports,
    BrnSelectImports,
    HlmButtonImports,
    HlmPopoverImports,
    HlmInputImports,
    BrnCommandImports,
    HlmCardImports,
    HlmAutocompleteImports,
    HlmButtonGroupImports,
    ExpansionPanel,
  ],
  templateUrl: './aptitude-group.component.html',
  providers: [
    provideIcons({ lucideTrash, lucideChevronDown }),
    provideHlmAutocompleteConfig({
      transformOptionToString: (option: Aptitude) => `${option.referentiel?.toUpperCase()} • ${option.nom}`,
      transformValueToSearch: (option: Aptitude) => `${option.referentiel?.toUpperCase()} • ${option.nom}`,
    }),
  ],
})
export class AptitudeGroupComponent {
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly #referentielStore = inject(ReferentielStore);

  readonly form = input.required<FormGroup>();
  readonly index = input.required<number | string>();
  readonly peutSupprimer = input<boolean, boolean | string>(false, { transform: booleanAttribute });

  readonly supprimerAptitude = output();

  readonly referentiels = computed(() => this.#referentielStore.entities());
  readonly aptitudes = computed(() =>
    this.#aptitudeStore.aptitudesFiltrées().filter((x) => x.nom.toLowerCase().includes(this.search().toLowerCase())),
  );

  readonly filtreReferentiel = signal<Référentiel | undefined>(undefined);
  public readonly search = signal('');

  readonly aptitudeActions: ExpansionPanelMenuAction<number | string>[] = [
    {
      icon: 'lucideTrash',
      label: 'Supprimer',
      class: 'invisible group-hover/item:visible',
      action: () => this.supprimerAptitude.emit(),
      invisible: (idx) => typeof idx === 'string',
    },
  ];

  setReferentiel(referentiel?: Référentiel) {
    this.filtreReferentiel.set(referentiel);
    this.#aptitudeStore.setFiltre(referentiel?.nom ?? '');
  }

  get aptitude() {
    return this.form().get('aptitude') as FormControl<Aptitude>;
  }
}
